/**
 * Bitcoin Historical Events Data
 * FUD (Fear, Uncertainty, Doubt) vs Positive milestones
 * Used in the Bitcoin Cycles page "BTC vs FUD" timeline
 */

export type EventType = 'fud' | 'positive' | 'neutral';

export interface BitcoinEvent {
  id: string;
  date: string; // ISO format
  timestamp: number;
  type: EventType;
  title: {
    fr: string;
    en: string;
  };
  description: {
    fr: string;
    en: string;
  };
  source?: string;
  priceAtEvent?: number; // BTC price at the time
  icon: string; // Emoji
}

// Historical Bitcoin events - FUD and Positive milestones
export const BITCOIN_HISTORICAL_EVENTS: BitcoinEvent[] = [
  // 2010-2011
  {
    id: 'first-btc-transaction',
    date: '2010-05-22',
    timestamp: new Date('2010-05-22').getTime(),
    type: 'positive',
    title: {
      fr: 'Première transaction réelle',
      en: 'First real transaction'
    },
    description: {
      fr: 'Laszlo Hanyecz achète 2 pizzas pour 10,000 BTC - la première transaction commerciale Bitcoin',
      en: 'Laszlo Hanyecz buys 2 pizzas for 10,000 BTC - the first Bitcoin commercial transaction'
    },
    priceAtEvent: 0.0025,
    icon: '🍕'
  },
  {
    id: 'mtgox-hack-2011',
    date: '2011-06-19',
    timestamp: new Date('2011-06-19').getTime(),
    type: 'fud',
    title: {
      fr: 'Hack Mt. Gox - BTC chute de 99%',
      en: 'Mt. Gox Hack - BTC crashes 99%'
    },
    description: {
      fr: 'Le prix passe de $17 à $0.01 en minutes suite au hack de Mt. Gox',
      en: 'Price drops from $17 to $0.01 in minutes following Mt. Gox hack'
    },
    priceAtEvent: 17,
    icon: '💀'
  },

  // 2013
  {
    id: 'bitcoin-dead-1',
    date: '2013-04-11',
    timestamp: new Date('2013-04-11').getTime(),
    type: 'fud',
    title: {
      fr: '"Bitcoin is Dead" #1',
      en: '"Bitcoin is Dead" #1'
    },
    description: {
      fr: 'Forbes déclare Bitcoin mort après une chute de 83%',
      en: 'Forbes declares Bitcoin dead after 83% crash'
    },
    source: 'Forbes',
    priceAtEvent: 55,
    icon: '📰'
  },
  {
    id: 'china-ban-1',
    date: '2013-12-05',
    timestamp: new Date('2013-12-05').getTime(),
    type: 'fud',
    title: {
      fr: 'La Chine interdit Bitcoin aux banques',
      en: 'China bans Bitcoin for banks'
    },
    description: {
      fr: 'La Banque Populaire de Chine interdit aux institutions financières de traiter des transactions Bitcoin',
      en: 'People\'s Bank of China bans financial institutions from handling Bitcoin transactions'
    },
    priceAtEvent: 1150,
    icon: '🇨🇳'
  },

  // 2014
  {
    id: 'mtgox-collapse',
    date: '2014-02-24',
    timestamp: new Date('2014-02-24').getTime(),
    type: 'fud',
    title: {
      fr: 'Faillite de Mt. Gox',
      en: 'Mt. Gox Bankruptcy'
    },
    description: {
      fr: 'Mt. Gox, le plus grand exchange, fait faillite après le vol de 850,000 BTC',
      en: 'Mt. Gox, the largest exchange, goes bankrupt after theft of 850,000 BTC'
    },
    priceAtEvent: 550,
    icon: '💔'
  },
  {
    id: 'newsweek-satoshi',
    date: '2014-03-06',
    timestamp: new Date('2014-03-06').getTime(),
    type: 'neutral',
    title: {
      fr: 'Newsweek "trouve" Satoshi',
      en: 'Newsweek "finds" Satoshi'
    },
    description: {
      fr: 'Newsweek prétend avoir trouvé Satoshi Nakamoto - il s\'avère que c\'était une erreur',
      en: 'Newsweek claims to have found Satoshi Nakamoto - turns out to be wrong'
    },
    priceAtEvent: 670,
    icon: '🔍'
  },

  // 2015
  {
    id: 'bitcoin-dead-2',
    date: '2015-01-14',
    timestamp: new Date('2015-01-14').getTime(),
    type: 'fud',
    title: {
      fr: '"Bitcoin is Dead" - The Guardian',
      en: '"Bitcoin is Dead" - The Guardian'
    },
    description: {
      fr: 'The Guardian déclare que Bitcoin est mort après une chute sous $200',
      en: 'The Guardian declares Bitcoin dead after drop below $200'
    },
    source: 'The Guardian',
    priceAtEvent: 185,
    icon: '☠️'
  },
  {
    id: 'ny-bitlicense',
    date: '2015-08-08',
    timestamp: new Date('2015-08-08').getTime(),
    type: 'neutral',
    title: {
      fr: 'New York lance BitLicense',
      en: 'New York launches BitLicense'
    },
    description: {
      fr: 'Premier cadre réglementaire américain pour les entreprises crypto',
      en: 'First US regulatory framework for crypto businesses'
    },
    priceAtEvent: 260,
    icon: '📜'
  },

  // 2016
  {
    id: 'halving-2',
    date: '2016-07-09',
    timestamp: new Date('2016-07-09').getTime(),
    type: 'positive',
    title: {
      fr: 'Deuxième Halving',
      en: 'Second Halving'
    },
    description: {
      fr: 'La récompense de bloc passe de 25 à 12.5 BTC',
      en: 'Block reward drops from 25 to 12.5 BTC'
    },
    priceAtEvent: 650,
    icon: '⚡'
  },
  {
    id: 'bitfinex-hack',
    date: '2016-08-02',
    timestamp: new Date('2016-08-02').getTime(),
    type: 'fud',
    title: {
      fr: 'Hack Bitfinex - 120,000 BTC volés',
      en: 'Bitfinex Hack - 120,000 BTC stolen'
    },
    description: {
      fr: 'Bitfinex perd 120,000 BTC dans un hack majeur',
      en: 'Bitfinex loses 120,000 BTC in major hack'
    },
    priceAtEvent: 600,
    icon: '🔓'
  },

  // 2017
  {
    id: 'japan-legal',
    date: '2017-04-01',
    timestamp: new Date('2017-04-01').getTime(),
    type: 'positive',
    title: {
      fr: 'Le Japon légalise Bitcoin',
      en: 'Japan legalizes Bitcoin'
    },
    description: {
      fr: 'Le Japon reconnaît officiellement Bitcoin comme moyen de paiement légal',
      en: 'Japan officially recognizes Bitcoin as a legal payment method'
    },
    priceAtEvent: 1080,
    icon: '🇯🇵'
  },
  {
    id: 'jamie-dimon-fraud',
    date: '2017-09-12',
    timestamp: new Date('2017-09-12').getTime(),
    type: 'fud',
    title: {
      fr: 'Jamie Dimon: "Bitcoin est une fraude"',
      en: 'Jamie Dimon: "Bitcoin is a fraud"'
    },
    description: {
      fr: 'Le PDG de JPMorgan déclare que Bitcoin est une fraude et promet de licencier tout employé qui en trade',
      en: 'JPMorgan CEO calls Bitcoin a fraud and promises to fire any employee trading it'
    },
    source: 'JPMorgan',
    priceAtEvent: 4200,
    icon: '🏦'
  },
  {
    id: 'china-ban-ico',
    date: '2017-09-04',
    timestamp: new Date('2017-09-04').getTime(),
    type: 'fud',
    title: {
      fr: 'La Chine interdit les ICO',
      en: 'China bans ICOs'
    },
    description: {
      fr: 'La Chine interdit toutes les Initial Coin Offerings',
      en: 'China bans all Initial Coin Offerings'
    },
    priceAtEvent: 4600,
    icon: '🚫'
  },
  {
    id: 'cme-futures',
    date: '2017-12-17',
    timestamp: new Date('2017-12-17').getTime(),
    type: 'positive',
    title: {
      fr: 'Lancement des futures CME',
      en: 'CME Futures Launch'
    },
    description: {
      fr: 'Le Chicago Mercantile Exchange lance les premiers contrats futures Bitcoin institutionnels',
      en: 'Chicago Mercantile Exchange launches first institutional Bitcoin futures'
    },
    priceAtEvent: 19783,
    icon: '📈'
  },
  {
    id: 'btc-ath-2017',
    date: '2017-12-17',
    timestamp: new Date('2017-12-17').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin atteint $20,000',
      en: 'ATH: Bitcoin reaches $20,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau record historique à près de $20,000',
      en: 'Bitcoin reaches new all-time high near $20,000'
    },
    priceAtEvent: 19783,
    icon: '🚀'
  },

  // 2018
  {
    id: 'bitcoin-dead-3',
    date: '2018-01-17',
    timestamp: new Date('2018-01-17').getTime(),
    type: 'fud',
    title: {
      fr: '"Bitcoin is Dead" - The Economist',
      en: '"Bitcoin is Dead" - The Economist'
    },
    description: {
      fr: 'The Economist déclare Bitcoin mort après une chute de 50%',
      en: 'The Economist declares Bitcoin dead after 50% crash'
    },
    source: 'The Economist',
    priceAtEvent: 11000,
    icon: '📰'
  },
  {
    id: 'warren-buffett-rat-poison',
    date: '2018-05-05',
    timestamp: new Date('2018-05-05').getTime(),
    type: 'fud',
    title: {
      fr: 'Warren Buffett: "Mort aux rats"',
      en: 'Warren Buffett: "Rat poison squared"'
    },
    description: {
      fr: 'Warren Buffett qualifie Bitcoin de "mort aux rats au carré"',
      en: 'Warren Buffett calls Bitcoin "rat poison squared"'
    },
    source: 'Berkshire Hathaway',
    priceAtEvent: 9700,
    icon: '🐀'
  },
  {
    id: 'nouriel-roubini-scam',
    date: '2018-10-11',
    timestamp: new Date('2018-10-11').getTime(),
    type: 'fud',
    title: {
      fr: 'Roubini: "La mère de toutes les arnaques"',
      en: 'Roubini: "Mother of all scams"'
    },
    description: {
      fr: 'L\'économiste Nouriel Roubini témoigne devant le Sénat US que Bitcoin est la "mère de toutes les arnaques"',
      en: 'Economist Nouriel Roubini testifies to US Senate that Bitcoin is the "mother of all scams"'
    },
    source: 'US Senate',
    priceAtEvent: 6300,
    icon: '🎭'
  },

  // 2019
  {
    id: 'bakkt-launch',
    date: '2019-09-23',
    timestamp: new Date('2019-09-23').getTime(),
    type: 'positive',
    title: {
      fr: 'Lancement de Bakkt',
      en: 'Bakkt Launch'
    },
    description: {
      fr: 'Bakkt (NYSE) lance les premiers futures Bitcoin physiquement réglés',
      en: 'Bakkt (NYSE) launches first physically-settled Bitcoin futures'
    },
    priceAtEvent: 9900,
    icon: '🏛️'
  },
  {
    id: 'trump-anti-bitcoin',
    date: '2019-07-12',
    timestamp: new Date('2019-07-12').getTime(),
    type: 'fud',
    title: {
      fr: 'Trump: "Je ne suis pas fan du Bitcoin"',
      en: 'Trump: "I am not a fan of Bitcoin"'
    },
    description: {
      fr: 'Le Président Trump tweete qu\'il n\'est pas fan du Bitcoin et des cryptomonnaies',
      en: 'President Trump tweets that he is not a fan of Bitcoin and cryptocurrencies'
    },
    source: 'Twitter',
    priceAtEvent: 11800,
    icon: '🇺🇸'
  },
  {
    id: 'china-ban-3',
    date: '2019-11-22',
    timestamp: new Date('2019-11-22').getTime(),
    type: 'fud',
    title: {
      fr: 'Nouvelle répression chinoise',
      en: 'New Chinese crackdown'
    },
    description: {
      fr: 'La Chine lance une nouvelle vague de répression contre les exchanges crypto',
      en: 'China launches new crackdown wave against crypto exchanges'
    },
    priceAtEvent: 7500,
    icon: '🇨🇳'
  },

  // 2020
  {
    id: 'covid-crash',
    date: '2020-03-12',
    timestamp: new Date('2020-03-12').getTime(),
    type: 'fud',
    title: {
      fr: 'Crash COVID - "Jeudi Noir"',
      en: 'COVID Crash - "Black Thursday"'
    },
    description: {
      fr: 'Bitcoin chute de 50% en 24h lors du krach boursier lié au COVID-19',
      en: 'Bitcoin drops 50% in 24h during COVID-19 market crash'
    },
    priceAtEvent: 4900,
    icon: '🦠'
  },
  {
    id: 'halving-3',
    date: '2020-05-11',
    timestamp: new Date('2020-05-11').getTime(),
    type: 'positive',
    title: {
      fr: 'Troisième Halving',
      en: 'Third Halving'
    },
    description: {
      fr: 'La récompense de bloc passe de 12.5 à 6.25 BTC',
      en: 'Block reward drops from 12.5 to 6.25 BTC'
    },
    priceAtEvent: 8600,
    icon: '⚡'
  },
  {
    id: 'microstrategy-buy',
    date: '2020-08-11',
    timestamp: new Date('2020-08-11').getTime(),
    type: 'positive',
    title: {
      fr: 'MicroStrategy achète $250M de BTC',
      en: 'MicroStrategy buys $250M of BTC'
    },
    description: {
      fr: 'MicroStrategy devient la première entreprise cotée à adopter Bitcoin comme actif de trésorerie',
      en: 'MicroStrategy becomes first public company to adopt Bitcoin as treasury asset'
    },
    priceAtEvent: 11800,
    icon: '🏢'
  },
  {
    id: 'paypal-bitcoin',
    date: '2020-10-21',
    timestamp: new Date('2020-10-21').getTime(),
    type: 'positive',
    title: {
      fr: 'PayPal adopte Bitcoin',
      en: 'PayPal adopts Bitcoin'
    },
    description: {
      fr: 'PayPal annonce le support de Bitcoin pour ses 346 millions d\'utilisateurs',
      en: 'PayPal announces Bitcoin support for its 346 million users'
    },
    priceAtEvent: 12800,
    icon: '💳'
  },

  // 2021
  {
    id: 'tesla-buy',
    date: '2021-02-08',
    timestamp: new Date('2021-02-08').getTime(),
    type: 'positive',
    title: {
      fr: 'Tesla achète $1.5B de BTC',
      en: 'Tesla buys $1.5B of BTC'
    },
    description: {
      fr: 'Tesla annonce un investissement de $1.5 milliard en Bitcoin',
      en: 'Tesla announces $1.5 billion investment in Bitcoin'
    },
    priceAtEvent: 46500,
    icon: '🚗'
  },
  {
    id: 'coinbase-ipo',
    date: '2021-04-14',
    timestamp: new Date('2021-04-14').getTime(),
    type: 'positive',
    title: {
      fr: 'IPO de Coinbase',
      en: 'Coinbase IPO'
    },
    description: {
      fr: 'Coinbase fait son entrée en bourse au NASDAQ avec une valorisation de $86 milliards',
      en: 'Coinbase goes public on NASDAQ with $86 billion valuation'
    },
    priceAtEvent: 63500,
    icon: '📊'
  },
  {
    id: 'elon-musk-flip',
    date: '2021-05-12',
    timestamp: new Date('2021-05-12').getTime(),
    type: 'fud',
    title: {
      fr: 'Elon Musk suspend les paiements Tesla',
      en: 'Elon Musk suspends Tesla payments'
    },
    description: {
      fr: 'Elon Musk annonce que Tesla n\'accepte plus Bitcoin à cause des préoccupations environnementales',
      en: 'Elon Musk announces Tesla no longer accepts Bitcoin due to environmental concerns'
    },
    source: 'Twitter',
    priceAtEvent: 54000,
    icon: '🔋'
  },
  {
    id: 'china-mining-ban',
    date: '2021-06-21',
    timestamp: new Date('2021-06-21').getTime(),
    type: 'fud',
    title: {
      fr: 'La Chine interdit le minage',
      en: 'China bans mining'
    },
    description: {
      fr: 'La Chine interdit complètement le minage de cryptomonnaies',
      en: 'China completely bans cryptocurrency mining'
    },
    priceAtEvent: 32700,
    icon: '⛏️'
  },
  {
    id: 'el-salvador-legal-tender',
    date: '2021-09-07',
    timestamp: new Date('2021-09-07').getTime(),
    type: 'positive',
    title: {
      fr: 'El Salvador: Bitcoin monnaie légale',
      en: 'El Salvador: Bitcoin legal tender'
    },
    description: {
      fr: 'El Salvador devient le premier pays à adopter Bitcoin comme monnaie légale officielle',
      en: 'El Salvador becomes first country to adopt Bitcoin as official legal tender'
    },
    priceAtEvent: 46800,
    icon: '🇸🇻'
  },
  {
    id: 'btc-ath-2021',
    date: '2021-11-10',
    timestamp: new Date('2021-11-10').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin atteint $69,000',
      en: 'ATH: Bitcoin reaches $69,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau record historique à $69,000',
      en: 'Bitcoin reaches new all-time high at $69,000'
    },
    priceAtEvent: 69000,
    icon: '🚀'
  },

  // 2022
  {
    id: 'russia-ukraine-war',
    date: '2022-02-24',
    timestamp: new Date('2022-02-24').getTime(),
    type: 'neutral',
    title: {
      fr: 'Guerre Russie-Ukraine',
      en: 'Russia-Ukraine War'
    },
    description: {
      fr: 'L\'Ukraine utilise Bitcoin pour lever des fonds de défense. La Russie explore les crypto pour contourner les sanctions',
      en: 'Ukraine uses Bitcoin to raise defense funds. Russia explores crypto to evade sanctions'
    },
    priceAtEvent: 38500,
    icon: '⚔️'
  },
  {
    id: 'luna-crash',
    date: '2022-05-09',
    timestamp: new Date('2022-05-09').getTime(),
    type: 'fud',
    title: {
      fr: 'Effondrement de LUNA/UST',
      en: 'LUNA/UST Collapse'
    },
    description: {
      fr: 'L\'écosystème Terra (LUNA/UST) s\'effondre, effaçant $60 milliards et déclenchant une contagion crypto',
      en: 'Terra ecosystem (LUNA/UST) collapses, wiping out $60 billion and triggering crypto contagion'
    },
    priceAtEvent: 30000,
    icon: '🌙'
  },
  {
    id: 'celsius-freeze',
    date: '2022-06-13',
    timestamp: new Date('2022-06-13').getTime(),
    type: 'fud',
    title: {
      fr: 'Celsius gèle les retraits',
      en: 'Celsius freezes withdrawals'
    },
    description: {
      fr: 'Celsius Network gèle les retraits de ses 1.7 million d\'utilisateurs',
      en: 'Celsius Network freezes withdrawals for its 1.7 million users'
    },
    priceAtEvent: 22500,
    icon: '🥶'
  },
  {
    id: 'three-arrows-collapse',
    date: '2022-06-27',
    timestamp: new Date('2022-06-27').getTime(),
    type: 'fud',
    title: {
      fr: 'Faillite de Three Arrows Capital',
      en: 'Three Arrows Capital Bankruptcy'
    },
    description: {
      fr: 'Le hedge fund crypto Three Arrows Capital fait faillite avec $3 milliards de dette',
      en: 'Crypto hedge fund Three Arrows Capital files for bankruptcy with $3 billion in debt'
    },
    priceAtEvent: 19000,
    icon: '📉'
  },
  {
    id: 'ecb-bitcoin-dead',
    date: '2022-11-30',
    timestamp: new Date('2022-11-30').getTime(),
    type: 'fud',
    title: {
      fr: 'BCE: "Les derniers soubresauts du Bitcoin"',
      en: 'ECB: "Bitcoin\'s last gasp"'
    },
    description: {
      fr: 'La Banque Centrale Européenne publie un article déclarant que Bitcoin est dans ses "derniers soubresauts"',
      en: 'European Central Bank publishes article claiming Bitcoin is in its "last gasp"'
    },
    source: 'ECB',
    priceAtEvent: 17200,
    icon: '🏛️'
  },
  {
    id: 'ftx-collapse',
    date: '2022-11-11',
    timestamp: new Date('2022-11-11').getTime(),
    type: 'fud',
    title: {
      fr: 'Effondrement de FTX',
      en: 'FTX Collapse'
    },
    description: {
      fr: 'FTX, deuxième exchange mondial, s\'effondre suite à des révélations de fraude. Sam Bankman-Fried arrêté',
      en: 'FTX, world\'s second-largest exchange, collapses amid fraud revelations. Sam Bankman-Fried arrested'
    },
    priceAtEvent: 16800,
    icon: '💥'
  },

  // 2023
  {
    id: 'silvergate-collapse',
    date: '2023-03-08',
    timestamp: new Date('2023-03-08').getTime(),
    type: 'fud',
    title: {
      fr: 'Faillite de Silvergate Bank',
      en: 'Silvergate Bank Collapse'
    },
    description: {
      fr: 'Silvergate, principale banque crypto, annonce sa liquidation volontaire',
      en: 'Silvergate, major crypto bank, announces voluntary liquidation'
    },
    priceAtEvent: 20200,
    icon: '🏦'
  },
  {
    id: 'sec-vs-binance-coinbase',
    date: '2023-06-05',
    timestamp: new Date('2023-06-05').getTime(),
    type: 'fud',
    title: {
      fr: 'SEC poursuit Binance et Coinbase',
      en: 'SEC sues Binance and Coinbase'
    },
    description: {
      fr: 'La SEC lance des poursuites contre Binance et Coinbase pour violation des lois sur les valeurs mobilières',
      en: 'SEC files lawsuits against Binance and Coinbase for securities law violations'
    },
    source: 'SEC',
    priceAtEvent: 26500,
    icon: '⚖️'
  },
  {
    id: 'blackrock-etf-filing',
    date: '2023-06-15',
    timestamp: new Date('2023-06-15').getTime(),
    type: 'positive',
    title: {
      fr: 'BlackRock dépose un ETF Bitcoin',
      en: 'BlackRock files Bitcoin ETF'
    },
    description: {
      fr: 'BlackRock, plus grand gestionnaire d\'actifs au monde, dépose une demande d\'ETF Bitcoin Spot',
      en: 'BlackRock, world\'s largest asset manager, files for Spot Bitcoin ETF'
    },
    priceAtEvent: 25500,
    icon: '📋'
  },
  {
    id: 'grayscale-wins-sec',
    date: '2023-08-29',
    timestamp: new Date('2023-08-29').getTime(),
    type: 'positive',
    title: {
      fr: 'Grayscale gagne contre la SEC',
      en: 'Grayscale wins against SEC'
    },
    description: {
      fr: 'Grayscale remporte son procès contre la SEC pour la conversion de GBTC en ETF',
      en: 'Grayscale wins lawsuit against SEC over GBTC to ETF conversion'
    },
    priceAtEvent: 27500,
    icon: '⚖️'
  },

  // 2024
  {
    id: 'spot-etf-approved',
    date: '2024-01-10',
    timestamp: new Date('2024-01-10').getTime(),
    type: 'positive',
    title: {
      fr: 'ETF Bitcoin Spot approuvés',
      en: 'Spot Bitcoin ETFs Approved'
    },
    description: {
      fr: 'La SEC approuve 11 ETF Bitcoin Spot, marquant une adoption institutionnelle historique',
      en: 'SEC approves 11 Spot Bitcoin ETFs, marking historic institutional adoption'
    },
    priceAtEvent: 46500,
    icon: '✅'
  },
  {
    id: 'halving-4',
    date: '2024-04-20',
    timestamp: new Date('2024-04-20').getTime(),
    type: 'positive',
    title: {
      fr: 'Quatrième Halving',
      en: 'Fourth Halving'
    },
    description: {
      fr: 'La récompense de bloc passe de 6.25 à 3.125 BTC',
      en: 'Block reward drops from 6.25 to 3.125 BTC'
    },
    priceAtEvent: 63800,
    icon: '⚡'
  },
  {
    id: 'btc-ath-2024-march',
    date: '2024-03-14',
    timestamp: new Date('2024-03-14').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $73,000',
      en: 'ATH: Bitcoin exceeds $73,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau record historique au-dessus de $73,000',
      en: 'Bitcoin reaches new all-time high above $73,000'
    },
    priceAtEvent: 73000,
    icon: '🚀'
  },
  {
    id: 'trump-bitcoin-conference',
    date: '2024-07-27',
    timestamp: new Date('2024-07-27').getTime(),
    type: 'positive',
    title: {
      fr: 'Trump pro-Bitcoin à Nashville',
      en: 'Trump pro-Bitcoin at Nashville'
    },
    description: {
      fr: 'Donald Trump prononce un discours pro-Bitcoin à la conférence Bitcoin de Nashville, promettant de faire des USA la "crypto capitale du monde"',
      en: 'Donald Trump delivers pro-Bitcoin speech at Nashville Bitcoin Conference, promising to make USA the "crypto capital of the world"'
    },
    priceAtEvent: 67500,
    icon: '🎤'
  },

  // 2025
  {
    id: 'trump-inauguration',
    date: '2025-01-20',
    timestamp: new Date('2025-01-20').getTime(),
    type: 'positive',
    title: {
      fr: 'Investiture de Trump - Ère crypto',
      en: 'Trump Inauguration - Crypto Era'
    },
    description: {
      fr: 'Donald Trump prend ses fonctions avec des promesses de réglementation crypto favorable',
      en: 'Donald Trump takes office with promises of favorable crypto regulation'
    },
    priceAtEvent: 102000,
    icon: '🏛️'
  },
  {
    id: 'trump-strategic-reserve',
    date: '2025-01-23',
    timestamp: new Date('2025-01-23').getTime(),
    type: 'positive',
    title: {
      fr: 'Décret: Réserve Stratégique Bitcoin',
      en: 'Executive Order: Strategic Bitcoin Reserve'
    },
    description: {
      fr: 'Trump signe un décret créant un groupe de travail sur les actifs numériques et une réserve stratégique de Bitcoin',
      en: 'Trump signs executive order creating Digital Asset working group and Strategic Bitcoin Reserve'
    },
    priceAtEvent: 103000,
    icon: '📜'
  },
  {
    id: 'sec-crypto-task-force',
    date: '2025-01-21',
    timestamp: new Date('2025-01-21').getTime(),
    type: 'positive',
    title: {
      fr: 'SEC: Task Force Crypto',
      en: 'SEC: Crypto Task Force'
    },
    description: {
      fr: 'La SEC lance une task force crypto sous la direction pro-crypto de Hester Peirce',
      en: 'SEC launches crypto task force under pro-crypto leadership of Hester Peirce'
    },
    priceAtEvent: 105000,
    icon: '🔄'
  },
  {
    id: 'microstrategy-500k-btc',
    date: '2025-02-10',
    timestamp: new Date('2025-02-10').getTime(),
    type: 'positive',
    title: {
      fr: 'MicroStrategy détient 500K BTC',
      en: 'MicroStrategy holds 500K BTC'
    },
    description: {
      fr: 'MicroStrategy franchit le cap des 500,000 BTC, devenant le plus grand détenteur institutionnel',
      en: 'MicroStrategy crosses 500,000 BTC milestone, becoming the largest institutional holder'
    },
    priceAtEvent: 97000,
    icon: '🏢'
  },
  {
    id: 'germany-bitcoin-reserve',
    date: '2025-02-28',
    timestamp: new Date('2025-02-28').getTime(),
    type: 'positive',
    title: {
      fr: 'L\'Allemagne étudie une réserve BTC',
      en: 'Germany considers BTC reserve'
    },
    description: {
      fr: 'Le Bundestag débat d\'une proposition de réserve stratégique Bitcoin inspirée des USA',
      en: 'Bundestag debates strategic Bitcoin reserve proposal inspired by USA'
    },
    priceAtEvent: 95000,
    icon: '🇩🇪'
  },
  {
    id: 'eth-etf-staking',
    date: '2025-03-15',
    timestamp: new Date('2025-03-15').getTime(),
    type: 'positive',
    title: {
      fr: 'SEC approuve ETF ETH avec staking',
      en: 'SEC approves ETH ETF with staking'
    },
    description: {
      fr: 'La SEC approuve les ETF Ethereum avec fonctionnalité de staking intégrée',
      en: 'SEC approves Ethereum ETFs with integrated staking functionality'
    },
    priceAtEvent: 88000,
    icon: '📈'
  },
  {
    id: 'bitcoin-100k-stable',
    date: '2025-03-20',
    timestamp: new Date('2025-03-20').getTime(),
    type: 'positive',
    title: {
      fr: 'Bitcoin stabilisé au-dessus de $100K',
      en: 'Bitcoin stabilizes above $100K'
    },
    description: {
      fr: 'Bitcoin maintient sa position au-dessus de $100,000 pendant 30 jours consécutifs',
      en: 'Bitcoin holds position above $100,000 for 30 consecutive days'
    },
    priceAtEvent: 102000,
    icon: '💎'
  },
  {
    id: 'ecb-cbdc-vs-btc',
    date: '2025-04-05',
    timestamp: new Date('2025-04-05').getTime(),
    type: 'fud',
    title: {
      fr: 'BCE: "L\'Euro numérique remplacera Bitcoin"',
      en: 'ECB: "Digital Euro will replace Bitcoin"'
    },
    description: {
      fr: 'La BCE affirme que l\'Euro numérique rendra Bitcoin obsolète - le marché ignore',
      en: 'ECB claims Digital Euro will make Bitcoin obsolete - market ignores'
    },
    source: 'ECB',
    priceAtEvent: 99000,
    icon: '🏛️'
  },
  {
    id: 'apple-pay-bitcoin',
    date: '2025-04-22',
    timestamp: new Date('2025-04-22').getTime(),
    type: 'positive',
    title: {
      fr: 'Apple Pay intègre Bitcoin',
      en: 'Apple Pay integrates Bitcoin'
    },
    description: {
      fr: 'Apple annonce l\'intégration native de Bitcoin dans Apple Pay pour 1.5 milliard d\'appareils',
      en: 'Apple announces native Bitcoin integration in Apple Pay for 1.5 billion devices'
    },
    priceAtEvent: 112000,
    icon: '🍎'
  },
  {
    id: 'btc-ath-2025-april',
    date: '2025-04-25',
    timestamp: new Date('2025-04-25').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $115,000',
      en: 'ATH: Bitcoin exceeds $115,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau record historique suite à l\'annonce Apple',
      en: 'Bitcoin reaches new all-time high following Apple announcement'
    },
    priceAtEvent: 115000,
    icon: '🚀'
  },
  {
    id: 'jpmorgan-bitcoin-custody',
    date: '2025-05-08',
    timestamp: new Date('2025-05-08').getTime(),
    type: 'positive',
    title: {
      fr: 'JPMorgan lance custody Bitcoin',
      en: 'JPMorgan launches Bitcoin custody'
    },
    description: {
      fr: 'JPMorgan Chase, dont le CEO avait qualifié Bitcoin de "fraude" en 2017, lance un service de garde',
      en: 'JPMorgan Chase, whose CEO called Bitcoin a "fraud" in 2017, launches custody service'
    },
    priceAtEvent: 108000,
    icon: '🏦'
  },
  {
    id: 'brazil-bitcoin-legal',
    date: '2025-05-20',
    timestamp: new Date('2025-05-20').getTime(),
    type: 'positive',
    title: {
      fr: 'Le Brésil adopte Bitcoin',
      en: 'Brazil adopts Bitcoin'
    },
    description: {
      fr: 'Le Brésil devient le deuxième pays à adopter Bitcoin comme monnaie légale après El Salvador',
      en: 'Brazil becomes second country to adopt Bitcoin as legal tender after El Salvador'
    },
    priceAtEvent: 118000,
    icon: '🇧🇷'
  },
  {
    id: 'warren-bitcoin-ban-fail',
    date: '2025-06-10',
    timestamp: new Date('2025-06-10').getTime(),
    type: 'fud',
    title: {
      fr: 'Warren: Projet de loi anti-crypto rejeté',
      en: 'Warren: Anti-crypto bill rejected'
    },
    description: {
      fr: 'Le projet de loi d\'Elizabeth Warren visant à restreindre les crypto est rejeté au Sénat',
      en: 'Elizabeth Warren\'s bill aimed at restricting crypto is rejected in Senate'
    },
    source: 'US Senate',
    priceAtEvent: 105000,
    icon: '⚖️'
  },
  {
    id: 'lightning-1m-tps',
    date: '2025-06-25',
    timestamp: new Date('2025-06-25').getTime(),
    type: 'positive',
    title: {
      fr: 'Lightning Network: 1M TPS',
      en: 'Lightning Network: 1M TPS'
    },
    description: {
      fr: 'Le Lightning Network atteint 1 million de transactions par seconde en pic',
      en: 'Lightning Network reaches 1 million transactions per second at peak'
    },
    priceAtEvent: 110000,
    icon: '⚡'
  },
  {
    id: 'blackrock-btc-treasury',
    date: '2025-07-15',
    timestamp: new Date('2025-07-15').getTime(),
    type: 'positive',
    title: {
      fr: 'BlackRock ajoute BTC à sa trésorerie',
      en: 'BlackRock adds BTC to treasury'
    },
    description: {
      fr: 'BlackRock annonce détenir Bitcoin directement dans sa trésorerie corporate',
      en: 'BlackRock announces holding Bitcoin directly in corporate treasury'
    },
    priceAtEvent: 122000,
    icon: '🏢'
  },
  {
    id: 'btc-ath-2025-july',
    date: '2025-07-20',
    timestamp: new Date('2025-07-20').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $125,000',
      en: 'ATH: Bitcoin exceeds $125,000'
    },
    description: {
      fr: 'Bitcoin atteint $125,000 suite aux adoptions institutionnelles massives',
      en: 'Bitcoin reaches $125,000 following massive institutional adoptions'
    },
    priceAtEvent: 125000,
    icon: '🚀'
  },
  {
    id: 'india-crypto-regulation',
    date: '2025-08-05',
    timestamp: new Date('2025-08-05').getTime(),
    type: 'positive',
    title: {
      fr: 'L\'Inde régule positivement les crypto',
      en: 'India positively regulates crypto'
    },
    description: {
      fr: 'L\'Inde abandonne son projet d\'interdiction et adopte un cadre réglementaire favorable',
      en: 'India abandons ban plans and adopts favorable regulatory framework'
    },
    priceAtEvent: 119000,
    icon: '🇮🇳'
  },
  {
    id: 'schiff-wrong-again',
    date: '2025-08-15',
    timestamp: new Date('2025-08-15').getTime(),
    type: 'fud',
    title: {
      fr: 'Peter Schiff: "C\'est la dernière bulle"',
      en: 'Peter Schiff: "This is the last bubble"'
    },
    description: {
      fr: 'Peter Schiff prédit pour la 100ème fois l\'effondrement imminent de Bitcoin',
      en: 'Peter Schiff predicts for the 100th time the imminent collapse of Bitcoin'
    },
    source: 'Twitter/X',
    priceAtEvent: 115000,
    icon: '🥇'
  },
  {
    id: 'visa-bitcoin-settlements',
    date: '2025-09-01',
    timestamp: new Date('2025-09-01').getTime(),
    type: 'positive',
    title: {
      fr: 'Visa: Règlements en Bitcoin',
      en: 'Visa: Bitcoin Settlements'
    },
    description: {
      fr: 'Visa commence à régler les transactions entre marchands en Bitcoin sur Lightning',
      en: 'Visa starts settling merchant transactions in Bitcoin on Lightning'
    },
    priceAtEvent: 128000,
    icon: '💳'
  },
  {
    id: 'uk-bitcoin-etf',
    date: '2025-09-15',
    timestamp: new Date('2025-09-15').getTime(),
    type: 'positive',
    title: {
      fr: 'Le Royaume-Uni approuve les ETF BTC',
      en: 'UK approves BTC ETFs'
    },
    description: {
      fr: 'La FCA britannique approuve les premiers ETF Bitcoin Spot au Royaume-Uni',
      en: 'UK FCA approves first Spot Bitcoin ETFs in United Kingdom'
    },
    priceAtEvent: 132000,
    icon: '🇬🇧'
  },
  {
    id: 'btc-ath-2025-sept',
    date: '2025-09-22',
    timestamp: new Date('2025-09-22').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $135,000',
      en: 'ATH: Bitcoin exceeds $135,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau sommet historique après l\'approbation UK',
      en: 'Bitcoin reaches new all-time high after UK approval'
    },
    priceAtEvent: 135000,
    icon: '🚀'
  },
  {
    id: 'china-unbans-bitcoin',
    date: '2025-10-10',
    timestamp: new Date('2025-10-10').getTime(),
    type: 'positive',
    title: {
      fr: 'La Chine lève l\'interdiction',
      en: 'China lifts the ban'
    },
    description: {
      fr: 'La Chine autorise à nouveau le trading de crypto dans des zones économiques spéciales',
      en: 'China allows crypto trading again in special economic zones'
    },
    priceAtEvent: 142000,
    icon: '🇨🇳'
  },
  {
    id: 'btc-ath-2025-oct',
    date: '2025-10-15',
    timestamp: new Date('2025-10-15').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $145,000',
      en: 'ATH: Bitcoin exceeds $145,000'
    },
    description: {
      fr: 'Bitcoin explose suite à l\'assouplissement chinois',
      en: 'Bitcoin surges following Chinese policy softening'
    },
    priceAtEvent: 145000,
    icon: '🚀'
  },
  {
    id: 'pension-funds-btc',
    date: '2025-10-28',
    timestamp: new Date('2025-10-28').getTime(),
    type: 'positive',
    title: {
      fr: 'Les fonds de pension investissent',
      en: 'Pension funds invest in BTC'
    },
    description: {
      fr: 'Plusieurs grands fonds de pension américains annoncent des allocations en Bitcoin',
      en: 'Several major US pension funds announce Bitcoin allocations'
    },
    priceAtEvent: 140000,
    icon: '💰'
  },
  {
    id: 'imf-bitcoin-acknowledgment',
    date: '2025-11-05',
    timestamp: new Date('2025-11-05').getTime(),
    type: 'neutral',
    title: {
      fr: 'Le FMI reconnaît Bitcoin',
      en: 'IMF acknowledges Bitcoin'
    },
    description: {
      fr: 'Le FMI publie un rapport reconnaissant Bitcoin comme "réserve de valeur émergente"',
      en: 'IMF publishes report acknowledging Bitcoin as "emerging store of value"'
    },
    source: 'IMF',
    priceAtEvent: 138000,
    icon: '🌐'
  },
  {
    id: 'amazon-bitcoin-payments',
    date: '2025-11-15',
    timestamp: new Date('2025-11-15').getTime(),
    type: 'positive',
    title: {
      fr: 'Amazon accepte Bitcoin',
      en: 'Amazon accepts Bitcoin'
    },
    description: {
      fr: 'Amazon annonce accepter les paiements Bitcoin via Lightning Network',
      en: 'Amazon announces accepting Bitcoin payments via Lightning Network'
    },
    priceAtEvent: 148000,
    icon: '📦'
  },
  {
    id: 'btc-ath-2025-nov',
    date: '2025-11-20',
    timestamp: new Date('2025-11-20').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $150,000',
      en: 'ATH: Bitcoin exceeds $150,000'
    },
    description: {
      fr: 'Bitcoin franchit le cap symbolique des $150,000',
      en: 'Bitcoin crosses the symbolic $150,000 milestone'
    },
    priceAtEvent: 150000,
    icon: '🚀'
  },
  {
    id: 'bitcoin-dead-478',
    date: '2025-11-25',
    timestamp: new Date('2025-11-25').getTime(),
    type: 'fud',
    title: {
      fr: '"Bitcoin is Dead" #478',
      en: '"Bitcoin is Dead" #478'
    },
    description: {
      fr: 'Un article déclare Bitcoin mort pour la 478ème fois selon 99bitcoins.com',
      en: 'An article declares Bitcoin dead for the 478th time according to 99bitcoins.com'
    },
    priceAtEvent: 147000,
    icon: '☠️'
  },
  {
    id: 'russia-crypto-reserve',
    date: '2025-12-05',
    timestamp: new Date('2025-12-05').getTime(),
    type: 'positive',
    title: {
      fr: 'La Russie crée une réserve crypto',
      en: 'Russia creates crypto reserve'
    },
    description: {
      fr: 'La Russie annonce la création d\'une réserve nationale en cryptomonnaies',
      en: 'Russia announces creation of national cryptocurrency reserve'
    },
    priceAtEvent: 155000,
    icon: '🇷🇺'
  },
  {
    id: 'etf-inflows-100b',
    date: '2025-12-15',
    timestamp: new Date('2025-12-15').getTime(),
    type: 'positive',
    title: {
      fr: 'ETF BTC: $100B d\'entrées',
      en: 'BTC ETFs: $100B inflows'
    },
    description: {
      fr: 'Les ETF Bitcoin Spot dépassent $100 milliards d\'entrées nettes cumulées',
      en: 'Spot Bitcoin ETFs exceed $100 billion in cumulative net inflows'
    },
    priceAtEvent: 158000,
    icon: '📊'
  },
  {
    id: 'btc-ath-2025-dec',
    date: '2025-12-20',
    timestamp: new Date('2025-12-20').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $160,000',
      en: 'ATH: Bitcoin exceeds $160,000'
    },
    description: {
      fr: 'Bitcoin termine 2025 sur un nouveau record historique au-dessus de $160,000',
      en: 'Bitcoin ends 2025 on a new all-time high above $160,000'
    },
    priceAtEvent: 160000,
    icon: '🚀'
  },
  {
    id: 'bitcoin-2025-summary',
    date: '2025-12-31',
    timestamp: new Date('2025-12-31').getTime(),
    type: 'positive',
    title: {
      fr: 'Bitcoin: +70% en 2025',
      en: 'Bitcoin: +70% in 2025'
    },
    description: {
      fr: 'Bitcoin clôture 2025 avec une performance de +70%, passant de ~$94K à $160K',
      en: 'Bitcoin closes 2025 with +70% performance, going from ~$94K to $160K'
    },
    priceAtEvent: 160000,
    icon: '🎉'
  },
  {
    id: 'btc-ath-2024-dec',
    date: '2024-12-17',
    timestamp: new Date('2024-12-17').getTime(),
    type: 'positive',
    title: {
      fr: 'ATH: Bitcoin dépasse $108,000',
      en: 'ATH: Bitcoin exceeds $108,000'
    },
    description: {
      fr: 'Bitcoin atteint un nouveau record historique au-dessus de $108,000',
      en: 'Bitcoin reaches new all-time high above $108,000'
    },
    priceAtEvent: 108000,
    icon: '🚀'
  },
  {
    id: 'peter-schiff-still-fud',
    date: '2024-11-15',
    timestamp: new Date('2024-11-15').getTime(),
    type: 'fud',
    title: {
      fr: 'Peter Schiff: "Bitcoin va s\'effondrer"',
      en: 'Peter Schiff: "Bitcoin will collapse"'
    },
    description: {
      fr: 'Peter Schiff continue de prédire l\'effondrement de Bitcoin alors qu\'il dépasse $90,000',
      en: 'Peter Schiff continues predicting Bitcoin collapse as it exceeds $90,000'
    },
    source: 'Twitter/X',
    priceAtEvent: 90000,
    icon: '🥇'
  },
];

// Helper function to get events within a date range
export function getEventsBetweenDates(startDate: string, endDate: string): BitcoinEvent[] {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();

  return BITCOIN_HISTORICAL_EVENTS.filter(
    event => event.timestamp >= start && event.timestamp <= end
  );
}

// Helper function to get events by type
export function getEventsByType(type: EventType): BitcoinEvent[] {
  return BITCOIN_HISTORICAL_EVENTS.filter(event => event.type === type);
}

// Get color for event type
export function getEventColor(type: EventType): string {
  switch (type) {
    case 'fud':
      return '#ef4444'; // Red
    case 'positive':
      return '#10b981'; // Green
    case 'neutral':
      return '#6b7280'; // Gray
  }
}

// Statistics
export const EVENT_STATS = {
  totalFudEvents: BITCOIN_HISTORICAL_EVENTS.filter(e => e.type === 'fud').length,
  totalPositiveEvents: BITCOIN_HISTORICAL_EVENTS.filter(e => e.type === 'positive').length,
  totalNeutralEvents: BITCOIN_HISTORICAL_EVENTS.filter(e => e.type === 'neutral').length,
  bitcoinDeadCount: BITCOIN_HISTORICAL_EVENTS.filter(e =>
    e.title.en.toLowerCase().includes('dead') ||
    e.description.en.toLowerCase().includes('dead')
  ).length,
  chinaBanCount: BITCOIN_HISTORICAL_EVENTS.filter(e =>
    e.title.en.toLowerCase().includes('china') &&
    (e.title.en.toLowerCase().includes('ban') || e.description.en.toLowerCase().includes('ban'))
  ).length,
};
